import React, { useState, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { api } from "../api";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, LabelList,
} from "recharts";
import "./Dashboard.css";
import "./AdminPanel.css";
import ActionPlanPanel from "./ActionPlanPanel";

const CATEGORY_COLORS: Record<string, string> = {
  COMPENSATION: "#4A90E2",
  OCCUPANCY:    "#e84393",
  OPERATING:    "#ea580c",
  ADVERTISING:  "#7c3aed",
  ALLOCATED:    "#475569",
};

const CATEGORY_ORDER = ["COMPENSATION", "OCCUPANCY", "OPERATING", "ADVERTISING", "ALLOCATED"];

// Categories that exist in raw data but should never appear on the dashboard
const EXCLUDED_CATEGORIES = new Set(["CORP_OH", "AMORT", "OTHERADJUSTMENTS"]);

interface AccountRow {
  account: string;
  category: string;
  budget: number;
  actual: number;
  variance: number;
  variancePct: number;
}

interface CategorySummary {
  category: string;
  budget: number;
  actual: number;
  variance: number;
  variancePct: number;
}

interface DashboardProps {
  costCenterNumber: string;
  costCenterName?: string;
}

// Which budget dimension is the reference for each month:
//   Apr–Jun  → Annual Budget
//   Jul–Sep  → July Forecast
//   Oct–Dec  → October Forecast
//   Jan–Mar  → January Forecast
const MONTH_BUDGET_DIM: Record<string, string> = {
  April: "Annual Budget",   May: "Annual Budget",      June: "Annual Budget",
  July:  "July Forecast",   August: "July Forecast",   September: "July Forecast",
  October: "October Forecast", November: "October Forecast", December: "October Forecast",
  January: "January Forecast", February: "January Forecast", March: "January Forecast",
};

// All unique budget dimensions we might need
const BUDGET_DIMS = ["Annual Budget", "July Forecast", "October Forecast", "January Forecast"] as const;

// Accounts written by syncComputedBudgets — used to detect phantom forecast rows
const COMPUTED_ACCT_NUMS = new Set(["60010", "60022", "60100", "60120", "60130"]);

/** True if a budget dimension has non-zero data for accounts beyond the 5 salary-computed ones */
function dimHasUploadedData(dimData: Record<string, any[]>): boolean {
  return Object.entries(dimData).some(([account, entries]) => {
    const num = account.split(" - ")[0].trim();
    if (COMPUTED_ACCT_NUMS.has(num)) return false;
    return entries.some((e: any) => (e.budget ?? 0) !== 0);
  });
}

const FISCAL_MONTHS_ALL = [
  "April","May","June","July","August","September",
  "October","November","December","January","February","March",
];
const FISCAL_MONTHS_SHORT = ["Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec","Jan","Feb","Mar"];

// Quarter mapping: which months belong to each fiscal quarter
const MONTH_TO_QUARTER: Record<string, { label: string; months: string[] }> = {
  April:     { label: "Q1", months: ["April", "May", "June"] },
  May:       { label: "Q1", months: ["April", "May", "June"] },
  June:      { label: "Q1", months: ["April", "May", "June"] },
  July:      { label: "Q2", months: ["July", "August", "September"] },
  August:    { label: "Q2", months: ["July", "August", "September"] },
  September: { label: "Q2", months: ["July", "August", "September"] },
  October:   { label: "Q3", months: ["October", "November", "December"] },
  November:  { label: "Q3", months: ["October", "November", "December"] },
  December:  { label: "Q3", months: ["October", "November", "December"] },
  January:   { label: "Q4", months: ["January", "February", "March"] },
  February:  { label: "Q4", months: ["January", "February", "March"] },
  March:     { label: "Q4", months: ["January", "February", "March"] },
};

const Dashboard: React.FC<DashboardProps> = ({ costCenterNumber, costCenterName }) => {
  const [availableMonths, setAvailableMonths] = useState<string[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<string>("");
  const [availableFYs, setAvailableFYs] = useState<string[]>([]);
  const [selectedFY, setSelectedFY] = useState<string>("");
  // budgetsByDim["Annual Budget"][account] = BudgetItem[]
  const [budgetsByDim, setBudgetsByDim] = useState<Record<string, Record<string, any[]>>>({});
  const [actualsRaw, setActualsRaw] = useState<Record<string, any[]>>({});
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedAccount, setSelectedAccount] = useState<string | null>(null);
  const [chartType, setChartType] = useState<"bar" | "line">("bar");
  const [hasBudgetData, setHasBudgetData] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [actionPlanOpen, setActionPlanOpen] = useState(false);
  const [actionPlanCount, setActionPlanCount] = useState<{ total: number; open: number }>({ total: 0, open: 0 });

  // Fetch available FYs on mount (no FY filter)
  useEffect(() => {
    if (!costCenterNumber) return;
    api.get("/budgets/months-with-actuals", { params: { cost_center: costCenterNumber } })
      .then(res => {
        const fys: string[] = res.data.fiscal_years || [];
        setAvailableFYs(fys);
        if (fys.length > 0 && !selectedFY) setSelectedFY(fys[0]); // default to most recent
      })
      .catch(err => console.error("Failed to fetch FYs:", err));
  }, [costCenterNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch which months have actuals (filtered by selected FY)
  useEffect(() => {
    if (!costCenterNumber || !selectedFY) return;
    api.get("/budgets/months-with-actuals", { params: { cost_center: costCenterNumber, fy: selectedFY } })
      .then(res => {
        const months: string[] = res.data.months || [];
        setAvailableMonths(months);
        if (months.length > 0) setSelectedMonth(months[months.length - 1]);
        else setSelectedMonth("");
      })
      .catch(err => console.error("Failed to fetch actuals months:", err));
  }, [costCenterNumber, selectedFY]);

  // Fetch all budget dimensions + Actuals (re-fetch when FY changes)
  useEffect(() => {
    if (!costCenterNumber || !selectedFY) return;
    setLoading(true);
    Promise.all([
      ...BUDGET_DIMS.map(dim =>
        api.get("/budgets", { params: { cost_center: costCenterNumber, dimension: dim, fy: selectedFY } })
      ),
      api.get("/budgets", { params: { cost_center: costCenterNumber, dimension: "Actuals", fy: selectedFY } }),
    ])
      .then(responses => {
        const dimMap: Record<string, Record<string, any[]>> = {};
        BUDGET_DIMS.forEach((dim, i) => { dimMap[dim] = responses[i].data; });
        setBudgetsByDim(dimMap);
        setActualsRaw(responses[BUDGET_DIMS.length].data);
        // Check if budget data actually has non-zero values
        const annualBudget = dimMap["Annual Budget"] || {};
        const hasAny = Object.values(annualBudget).some(months =>
          months.some((m: any) => m.budget !== 0)
        );
        setHasBudgetData(hasAny);
        setLoading(false);
      })
      .catch(err => {
        console.error("Failed to fetch dashboard data:", err);
        setLoading(false);
      });
  }, [costCenterNumber, selectedFY]);

  // Reset category/account filter when month changes
  useEffect(() => { setSelectedCategory(null); setSelectedAccount(null); }, [selectedMonth]);
  // Reset account filter when category filter changes
  useEffect(() => { setSelectedAccount(null); }, [selectedCategory]);

  // Fetch action plan count for current month
  const fetchActionCount = useCallback(() => {
    if (!costCenterNumber || !selectedFY || !selectedMonth || selectedMonth === "YTD") {
      setActionPlanCount({ total: 0, open: 0 });
      return;
    }
    api.get("/action-plans", { params: { cost_center: costCenterNumber, fy: selectedFY, month: selectedMonth } })
      .then(res => {
        const items = res.data as { status: string }[];
        setActionPlanCount({ total: items.length, open: items.filter(a => a.status === "OPEN").length });
      })
      .catch(() => setActionPlanCount({ total: 0, open: 0 }));
  }, [costCenterNumber, selectedFY, selectedMonth]);

  useEffect(() => { fetchActionCount(); }, [fetchActionCount]);

  // For YTD, sum across all available months; otherwise use the single selected month
  const monthsToSum = selectedMonth === "YTD" ? availableMonths : (selectedMonth ? [selectedMonth] : []);

  // Per-account rows — budget reference is the correct dimension for each month
  const accountRows: AccountRow[] = useMemo(() => {
    if (monthsToSum.length === 0) return [];
    const allAccounts = Array.from(new Set([
      ...Object.values(budgetsByDim).flatMap(d => Object.keys(d)),
      ...Object.keys(actualsRaw),
    ]));
    return allAccounts
      .map(account => {
        let budget = 0, actual = 0;
        let category = "OTHER";
        for (const month of monthsToSum) {
          const dim = MONTH_BUDGET_DIM[month] ?? "Annual Budget";
          const dimData = budgetsByDim[dim] ?? {};
          const budgetEntry = dimData[account]?.find((m: any) => m.month === month);
          const actualEntry = actualsRaw[account]?.find((m: any) => m.month === month);
          budget += budgetEntry?.budget ?? 0;
          actual += actualEntry?.budget ?? 0;
          if (category === "OTHER") {
            category = (budgetEntry?.category || actualEntry?.category || "OTHER").toUpperCase();
          }
        }
        const variance    = budget - actual;
        const variancePct = budget !== 0 ? (variance / Math.abs(budget)) * 100 : 0;
        return { account, category, budget, actual, variance, variancePct };
      })
      .filter(r => !EXCLUDED_CATEGORIES.has(r.category))
      .sort((a, b) => {
        const ai = CATEGORY_ORDER.indexOf(a.category);
        const bi = CATEGORY_ORDER.indexOf(b.category);
        const catDiff = (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
        return catDiff !== 0 ? catDiff : a.account.localeCompare(b.account);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [budgetsByDim, actualsRaw, selectedMonth, availableMonths]);

  // Category summaries (excluded categories already filtered out of accountRows)
  const categorySummaries: CategorySummary[] = useMemo(() => {
    const map: Record<string, { budget: number; actual: number }> = {};
    accountRows.forEach(({ category, budget, actual }) => {
      if (!map[category]) map[category] = { budget: 0, actual: 0 };
      map[category].budget += budget;
      map[category].actual += actual;
    });
    return Object.entries(map)
      .sort(([a], [b]) => {
        const ai = CATEGORY_ORDER.indexOf(a);
        const bi = CATEGORY_ORDER.indexOf(b);
        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
      })
      .map(([category, { budget, actual }]) => {
        const variance    = budget - actual;
        const variancePct = budget !== 0 ? (variance / Math.abs(budget)) * 100 : 0;
        return { category, budget, actual, variance, variancePct };
      });
  }, [accountRows]);

  // Rows filtered by selected category (for the tables)
  const filteredRows = useMemo(() =>
    selectedCategory ? accountRows.filter(r => r.category === selectedCategory) : accountRows,
    [accountRows, selectedCategory]
  );

  // Per-account detail rows with quarterly budget, QTD utilization %, and YTG
  const accountDetailRows = useMemo(() => {
    if (!selectedMonth || selectedMonth === "YTD" || !Object.keys(budgetsByDim).length) return accountRows.map(r => ({ ...r, qtrBudget: r.budget, utilPct: r.budget > 0 ? (r.actual / r.budget) * 100 : 0, ytgBudget: 0, annualBudget: 0 }));
    const qtr = MONTH_TO_QUARTER[selectedMonth];
    if (!qtr) return accountRows.map(r => ({ ...r, qtrBudget: r.budget, utilPct: 0, ytgBudget: 0, annualBudget: 0 }));
    const currentMonthIdx = FISCAL_MONTHS_ALL.indexOf(selectedMonth);
    // Remaining months from current month to end of FY (inclusive of current)
    const ytgMonths = FISCAL_MONTHS_ALL.slice(currentMonthIdx);
    // Quarter months up to and including selected month (QTD)
    const qtdMonths = qtr.months.filter(m => {
      const mIdx = FISCAL_MONTHS_ALL.indexOf(m);
      return mIdx <= currentMonthIdx;
    });

    return accountRows.map(row => {
      let qtrBudget = 0;
      let ytgBudget = 0;
      let qtdActual = 0;
      // Sum quarter budget across all quarter months
      for (const m of qtr.months) {
        const dim = MONTH_BUDGET_DIM[m] ?? "Annual Budget";
        const dimData = budgetsByDim[dim] ?? {};
        const entry = dimData[row.account]?.find((e: any) => e.month === m);
        qtrBudget += entry?.budget ?? 0;
      }
      // Sum QTD actuals (quarter months up to selected month)
      for (const m of qtdMonths) {
        if (availableMonths.includes(m)) {
          const ae = actualsRaw[row.account]?.find((e: any) => e.month === m);
          qtdActual += ae?.budget ?? 0;
        }
      }
      // Sum YTG budget from current month to end of FY
      for (const m of ytgMonths) {
        const dim = MONTH_BUDGET_DIM[m] ?? "Annual Budget";
        const dimData = budgetsByDim[dim] ?? {};
        const entry = dimData[row.account]?.find((e: any) => e.month === m);
        ytgBudget += entry?.budget ?? 0;
      }
      // Sum annual budget across all 12 fiscal months
      let annualBudget = 0;
      for (const m of FISCAL_MONTHS_ALL) {
        const dim = MONTH_BUDGET_DIM[m] ?? "Annual Budget";
        const dimData = budgetsByDim[dim] ?? {};
        const entry = dimData[row.account]?.find((e: any) => e.month === m);
        annualBudget += entry?.budget ?? 0;
      }
      const utilPct = qtrBudget > 0 ? (qtdActual / qtrBudget) * 100 : 0;
      return { ...row, qtrBudget, utilPct, ytgBudget, annualBudget };
    });
  }, [accountRows, selectedMonth, budgetsByDim, actualsRaw, availableMonths]);

  // ── Category variance table data (ActualsDashboard style) ────────────────
  const categoryVarData = useMemo(() => {
    // Compute YTD actual per account (sum across ALL available months)
    const ytdActualByAcct: Record<string, number> = {};
    const allAccts = Array.from(new Set([
      ...Object.values(budgetsByDim).flatMap(d => Object.keys(d)),
      ...Object.keys(actualsRaw),
    ]));
    for (const account of allAccts) {
      let ytdA = 0;
      for (const month of availableMonths) {
        const ae = actualsRaw[account]?.find((m: any) => m.month === month);
        ytdA += ae?.budget ?? 0;
      }
      ytdActualByAcct[account] = ytdA;
    }

    return CATEGORY_ORDER
      .filter(cat => accountDetailRows.some(r => r.category === cat))
      .map(cat => {
        const rows = accountDetailRows.filter(r => r.category === cat);
        const budget       = rows.reduce((s, r) => s + r.budget, 0);
        const actual       = rows.reduce((s, r) => s + r.actual, 0);
        const qtrBudget    = rows.reduce((s, r) => s + r.qtrBudget, 0);
        const ytgBudget    = rows.reduce((s, r) => s + r.ytgBudget, 0);
        const annualBudget = rows.reduce((s, r) => s + r.annualBudget, 0);
        const ytdActual = rows.reduce((s, r) => s + (ytdActualByAcct[r.account] || 0), 0);
        const variance  = budget - actual;
        const utilBase  = selectedMonth === "YTD" ? budget : qtrBudget;
        const utilPct   = utilBase > 0 ? (ytdActual / utilBase) * 100 : 0;
        const ytg       = annualBudget - ytdActual;

        // Account breakdown bar: non-zero variance accounts, sorted by |variance|
        const acctVars = rows
          .map(r => ({ account: r.account, variance: r.budget - r.actual }))
          .filter(a => a.variance !== 0)
          .sort((a, b) => Math.abs(b.variance) - Math.abs(a.variance));
        const totalAbsVar = acctVars.reduce((s, a) => s + Math.abs(a.variance), 0) || 1;

        return { category: cat, budget, actual, variance, ytdActual, qtrBudget, ytgBudget, utilPct, ytg, acctVars, totalAbsVar, accounts: rows };
      });
  }, [accountDetailRows, budgetsByDim, actualsRaw, availableMonths, selectedMonth]);

  // Quarterly label for header
  const quarterLabel = useMemo(() => {
    if (!selectedMonth || selectedMonth === "YTD") return null;
    return MONTH_TO_QUARTER[selectedMonth]?.label ?? null;
  }, [selectedMonth]);

  // ── Spend trend chart data ───────────────────────────────────────────────
  const spendChartMonthly = useMemo(() => {
    if (!Object.keys(budgetsByDim).length) return [];
    const allAccounts = Array.from(new Set([
      ...Object.values(budgetsByDim).flatMap(d => Object.keys(d)),
      ...Object.keys(actualsRaw),
    ]));
    const annualDimData = budgetsByDim["Annual Budget"] ?? {};
    // Pre-check which forecast dimensions have real uploaded data (not just salary-computed)
    const uploadedDims = new Set<string>(["Annual Budget"]);
    for (const dim of BUDGET_DIMS) {
      if (dim !== "Annual Budget" && dimHasUploadedData(budgetsByDim[dim] ?? {})) {
        uploadedDims.add(dim);
      }
    }
    return FISCAL_MONTHS_ALL.map((month, idx) => {
      // Pick the appropriate forecast dimension for this specific month
      const rawForecastDim = MONTH_BUDGET_DIM[month] ?? "Annual Budget";
      // Only use the forecast dimension if it was actually uploaded
      const forecastDim = uploadedDims.has(rawForecastDim) ? rawForecastDim : "Annual Budget";
      const forecastDimData = budgetsByDim[forecastDim] ?? {};
      let mBudgetAnnual = 0, mBudgetForecast = 0, mActual = 0;
      const hasActuals = availableMonths.includes(month);
      for (const acc of allAccounts) {
        const beForecast = forecastDimData[acc]?.find((e: any) => e.month === month);
        const beAnnual   = annualDimData[acc]?.find((e: any) => e.month === month);
        const ae         = hasActuals ? actualsRaw[acc]?.find((e: any) => e.month === month) : undefined;
        const category = ((beForecast?.category || beAnnual?.category || ae?.category) ?? "OTHER").toUpperCase();
        if (EXCLUDED_CATEGORIES.has(category)) continue;
        if (selectedCategory && category !== selectedCategory) continue;
        if (selectedAccount && acc !== selectedAccount) continue;
        mBudgetAnnual   += beAnnual?.budget ?? 0;
        mBudgetForecast += beForecast?.budget ?? 0;
        if (hasActuals) mActual += ae?.budget ?? 0;
      }
      return { month, short: FISCAL_MONTHS_SHORT[idx], forecastDim, idx, mBudgetAnnual, mBudgetForecast, mActual: hasActuals ? mActual : null };
    });
  }, [budgetsByDim, actualsRaw, availableMonths, selectedCategory, selectedAccount]);

  // Reference dimension label for the header
  const refDimLabel = useMemo(() => {
    if (!selectedMonth) return null;
    if (selectedMonth === "YTD") {
      const dimsRaw = availableMonths.map(m => MONTH_BUDGET_DIM[m] ?? "Annual Budget");
      const dims = dimsRaw.filter((d, i) => dimsRaw.indexOf(d) === i);
      return dims.length > 1 ? `${dims[0]} → ${dims[dims.length - 1]}` : (dims[0] ?? null);
    }
    return MONTH_BUDGET_DIM[selectedMonth] ?? "Annual Budget";
  }, [selectedMonth, availableMonths]);

  // Totals
  const totalBudget   = categorySummaries.reduce((s, c) => s + c.budget, 0);
  const totalActual   = categorySummaries.reduce((s, c) => s + c.actual, 0);
  const totalVariance = totalBudget - totalActual;

  // Full-year budget (all 12 months) for utilization % denominator
  const fullYearBudget = useMemo(() => {
    const allAccounts = Array.from(new Set(
      Object.values(budgetsByDim).flatMap(d => Object.keys(d))
    ));
    let total = 0;
    for (const month of FISCAL_MONTHS_ALL) {
      const dim = MONTH_BUDGET_DIM[month] ?? "Annual Budget";
      const dimData = budgetsByDim[dim] ?? {};
      for (const acc of allAccounts) {
        const entry = dimData[acc]?.find((e: any) => e.month === month);
        if (!entry) continue;
        const cat = (entry.category || "OTHER").toUpperCase();
        if (EXCLUDED_CATEGORIES.has(cat)) continue;
        if (selectedCategory && cat !== selectedCategory) continue;
        if (selectedAccount && acc !== selectedAccount) continue;
        total += entry.budget ?? 0;
      }
    }
    return total;
  }, [budgetsByDim, selectedCategory, selectedAccount]);

  const totalUtilPct  = fullYearBudget > 0 ? (totalActual / fullYearBudget) * 100 : 0;

  // Top 5 unfavorable: actuals > budget (variance most negative)
  const top5Unfavorable = [...filteredRows]
    .filter(r => r.variance < 0)
    .sort((a, b) => a.variance - b.variance)
    .slice(0, 5);

  // Top 5 favorable: actuals < budget (variance most positive)
  const top5Favorable = [...filteredRows]
    .filter(r => r.variance > 0)
    .sort((a, b) => b.variance - a.variance)
    .slice(0, 5);

  // Formatting helpers
  const fmt = (v: number) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(Math.abs(v));

  const fmtVar = (v: number) => (v < 0 ? `(${fmt(v)})` : fmt(v));

  // Subtitle text for header portal
  const subtitleText = selectedMonth === "YTD" && availableMonths.length > 0
    ? `${selectedFY} YTD (${availableMonths[0]} – ${availableMonths[availableMonths.length - 1]}) — Budget vs Actuals`
    : selectedMonth
    ? `${selectedFY} ${selectedMonth} — Budget vs Actuals`
    : selectedFY
    ? `${selectedFY} — Select a month to view`
    : "Select a fiscal year to begin";

  const subtitleSlot = document.getElementById("page-subtitle-slot");
  const controlsSlot = document.getElementById("page-controls-slot");

  const pickerControls = (
    <div className="dash-controls-bar">
      <div className="dash-controls-pickers">
        {availableFYs.length > 0 && (
          <div className="dash-month-picker">
            <span className="dash-month-label">FY</span>
            <select
              value={selectedFY}
              onChange={e => setSelectedFY(e.target.value)}
              className="dash-month-select"
            >
              {availableFYs.map(fy => <option key={fy} value={fy}>{fy}</option>)}
            </select>
          </div>
        )}
        <div className="dash-month-picker">
          <span className="dash-month-label">Month</span>
          <select
            value={selectedMonth}
            onChange={e => setSelectedMonth(e.target.value)}
            className="dash-month-select"
          >
            {availableMonths.length === 0 && <option value="">No actuals available</option>}
            {availableMonths.map(m => <option key={m} value={m}>{m}</option>)}
            {availableMonths.length > 0 && (
              <option value="YTD">YTD ({availableMonths[0]}–{availableMonths[availableMonths.length - 1]})</option>
            )}
          </select>
        </div>
      </div>
      {selectedMonth && selectedMonth !== "YTD" && (
        <button className="ap-trigger" onClick={() => setActionPlanOpen(true)}>
          <span className="material-icons">checklist</span>
          Action Plan
          {actionPlanCount.total > 0 && (
            <span className={`ap-trigger-badge${actionPlanCount.open > 0 ? " ap-trigger-badge--has-open" : ""}`}>
              {actionPlanCount.open > 0 ? `${actionPlanCount.open} open` : `${actionPlanCount.total}`}
            </span>
          )}
        </button>
      )}
    </div>
  );

  return (
    <div className="dash-container">

      {/* Portal subtitle into header row 2 */}
      {subtitleSlot && createPortal(
        <span className="app-header-page-subtitle">{subtitleText}</span>,
        subtitleSlot
      )}

      {/* Portal FY/Month pickers into header row 2 */}
      {controlsSlot && createPortal(pickerControls, controlsSlot)}

      {loading && (
        <div className="dash-loading">
          <span className="material-icons dash-loading-icon">hourglass_top</span>
          Loading dashboard data…
        </div>
      )}

      {/* ── No actuals empty state ───────────────────────────────────────── */}
      {!loading && !selectedMonth && selectedFY && (
        <div className="dash-info-banner">
          <span className="material-icons">info</span>
          <span>No actuals data available for <strong>{selectedFY}</strong>. Upload monthly actuals from the <strong>Data Upload</strong> tab to see budget vs actual comparisons.</span>
        </div>
      )}

      {!loading && selectedMonth && (
        <>
          {/* ── No budget data warning ────────────────────────────────────── */}
          {!hasBudgetData && (
            <div className="dash-info-banner dash-info-banner--warn">
              <span className="material-icons">warning</span>
              <span>No Annual Budget data found for <strong>{selectedFY}</strong>. Budget columns will show $0. Upload a budget from the <strong>Data Upload</strong> tab.</span>
            </div>
          )}

          {/* ── KPI Summary Strip (matches ActualsDashboard style) ─────── */}
          <div className="dash-kpi-strip">
            <div className="dash-kpi-card">
              <div className="dash-kpi-label">
                <span className="material-icons">account_balance</span>
                {selectedMonth === "YTD" ? "YTD" : selectedMonth} Budget
              </div>
              <div className="dash-kpi-value">{fmt(totalBudget)}</div>
              <div className="dash-kpi-sub">{refDimLabel ?? "Budget reference"}</div>
            </div>
            <div className="dash-kpi-card">
              <div className="dash-kpi-label">
                <span className="material-icons">payments</span>
                {selectedMonth === "YTD" ? "YTD" : selectedMonth} Actual
              </div>
              <div className="dash-kpi-value">{fmt(totalActual)}</div>
              <div className="dash-kpi-sub">
                {selectedMonth === "YTD" ? `Apr \u2013 ${availableMonths[availableMonths.length - 1] || "\u2014"}` : `${selectedMonth} actual spend`}
              </div>
            </div>
            <div className={`dash-kpi-card ${totalVariance < 0 ? "dash-kpi-card--warn" : "dash-kpi-card--accent"}`}>
              <div className={`dash-kpi-label ${totalVariance < 0 ? "dash-kpi-label--red" : "dash-kpi-label--green"}`}>
                <span className="material-icons">{totalVariance < 0 ? "warning" : "check_circle"}</span>
                {totalVariance < 0 ? "Over Budget" : "Under Budget"}
              </div>
              <div className={`dash-kpi-value ${totalVariance < 0 ? "dash-kpi-value--red" : "dash-kpi-value--green"}`}>
                {fmtVar(totalVariance)}
              </div>
              <div className={`dash-kpi-sub ${totalVariance < 0 ? "dash-kpi-sub--red" : "dash-kpi-sub--green"}`}>
                {selectedMonth === "YTD" ? "YTD" : selectedMonth} actual vs budget
              </div>
            </div>
            <div className="dash-kpi-card">
              <div className="dash-kpi-label">
                <span className="material-icons">speed</span>
                Budget Utilization
              </div>
              <div className={`dash-kpi-value ${totalUtilPct > 100 ? "dash-kpi-value--red" : "dash-kpi-value--blue"}`}>
                {Math.round(totalUtilPct)}%
              </div>
              <div className="dash-kpi-sub">of full-year budget consumed</div>
            </div>
          </div>

          {/* ── Category KPI Cards ─────────────────────────────────────────── */}
          <div className="dash-cat-strip">

            {/* Total card */}
            <div
              className={`dash-cat-card dash-cat-card--total${selectedCategory === null ? " dash-cat-card--active" : ""}`}
              onClick={() => setSelectedCategory(null)}
              style={{ cursor: "pointer" }}
            >
              <div className="dash-cat-name">Total Spend</div>
              <div className="dash-cat-rows">
                <div className="dash-cat-row">
                  <span className="dash-cat-row-label">{refDimLabel ?? "Budget"}</span>
                  <span className="dash-cat-row-val">{fmt(totalBudget)}</span>
                </div>
                <div className="dash-cat-row">
                  <span className="dash-cat-row-label">Actuals</span>
                  <span className="dash-cat-row-val">{fmt(totalActual)}</span>
                </div>
                <div className={`dash-cat-row dash-cat-row--var ${totalVariance >= 0 ? "dash-fav" : "dash-unfav"}`}>
                  <span className="dash-cat-row-label">Variance</span>
                  <span className="dash-cat-row-val">{fmtVar(totalVariance)}</span>
                </div>
              </div>
              <div className="dash-progress-wrap">
                <div
                  className="dash-progress-fill"
                  style={{
                    transform: `scaleX(${Math.min(100, totalUtilPct) / 100})`,
                    background: totalActual > fullYearBudget ? "#dc2626" : "#16a34a",
                  }}
                />
              </div>
              <div className="dash-progress-label">
                {Math.round(totalUtilPct)}% of budget utilized
              </div>
            </div>

            {/* Divider between Total and category cards */}
            <div className="dash-cat-divider" />

            {/* Per-category cards */}
            {categorySummaries.map(cat => {
              const color     = CATEGORY_COLORS[cat.category] ?? "#64748b";
              const utilPct   = cat.budget > 0 ? (cat.actual / cat.budget) * 100 : 0;
              const isActive  = selectedCategory === cat.category;
              return (
                <div
                  key={cat.category}
                  className={`dash-cat-card${isActive ? " dash-cat-card--active" : ""}`}
                  style={{ cursor: "pointer" }}
                  onClick={() => setSelectedCategory(isActive ? null : cat.category)}
                >
                  <div className="dash-cat-name"><span className="dash-cat-dot" style={{ background: color }} />{cat.category}</div>
                  <div className="dash-cat-rows">
                    <div className="dash-cat-row">
                      <span className="dash-cat-row-label">{refDimLabel ?? "Budget"}</span>
                      <span className="dash-cat-row-val">{fmt(cat.budget)}</span>
                    </div>
                    <div className="dash-cat-row">
                      <span className="dash-cat-row-label">Actuals</span>
                      <span className="dash-cat-row-val">{fmt(cat.actual)}</span>
                    </div>
                    <div className={`dash-cat-row dash-cat-row--var ${cat.variance >= 0 ? "dash-fav" : "dash-unfav"}`}>
                      <span className="dash-cat-row-label">Variance</span>
                      <span className="dash-cat-row-val">{fmtVar(cat.variance)}</span>
                    </div>
                  </div>
                  <div className="dash-progress-wrap">
                    <div
                      className="dash-progress-fill"
                      style={{
                        transform: `scaleX(${Math.min(100, utilPct) / 100})`,
                        background: utilPct > 100 ? "#dc2626" : color,
                      }}
                    />
                  </div>
                  <div className="dash-progress-label">{Math.round(utilPct)}% utilized</div>
                </div>
              );
            })}
          </div>

          {/* ── Account Tables ─────────────────────────────────────────────── */}
          <div className="dash-tables-section">
            <div className="dash-tables-section-header">
              <span className="material-icons">assessment</span>
              <span className="dash-tables-section-title">Current Forecast Status</span>
            </div>
            <div className="dash-tables-section-inner">
          <div className="dash-tables-row">

            {/* Over Budget */}
            <div className="dash-table-card dash-table-card--unfav">
              <div className="dash-table-head">
                <span className="material-icons">trending_up</span>
                Top 5 Over Budget
                {selectedCategory && (
                  <span className="dash-table-filter-badge" style={{ background: (CATEGORY_COLORS[selectedCategory] ?? "#64748b") + "22", color: CATEGORY_COLORS[selectedCategory] ?? "#64748b" }}>
                    {selectedCategory}
                  </span>
                )}
              </div>
              <table className="dash-tbl">
                <thead>
                  <tr>
                    <th className="dash-th-left">Account</th>
                    <th>Budget</th>
                    <th>Actuals</th>
                    <th>Variance</th>
                    <th className="dash-th-center">Category</th>
                  </tr>
                </thead>
                <tbody>
                  {top5Unfavorable.map((row, i) => {
                    const color = CATEGORY_COLORS[row.category] ?? "#64748b";
                    return (
                      <tr key={row.account} className={i % 2 === 1 ? "dash-tr-alt" : ""}>
                        <td className="dash-td-account" title={row.account}>{row.account}</td>
                        <td className="dash-td-num">{fmt(row.budget)}</td>
                        <td className="dash-td-num">{fmt(row.actual)}</td>
                        <td className="dash-td-num dash-unfav">{fmtVar(row.variance)}</td>
                        <td className="dash-td-center">
                          <span className="dash-badge" style={{ background: color + "18", color }}>
                            {row.category}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                  {top5Unfavorable.length === 0 && (
                    <tr>
                      <td colSpan={5} className="dash-td-empty">
                        <span className="material-icons">check_circle</span>
                        All accounts within budget
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Under Budget */}
            <div className="dash-table-card dash-table-card--fav">
              <div className="dash-table-head">
                <span className="material-icons">trending_down</span>
                Top 5 Under Budget
                {selectedCategory && (
                  <span className="dash-table-filter-badge" style={{ background: (CATEGORY_COLORS[selectedCategory] ?? "#64748b") + "22", color: CATEGORY_COLORS[selectedCategory] ?? "#64748b" }}>
                    {selectedCategory}
                  </span>
                )}
              </div>
              <table className="dash-tbl">
                <thead>
                  <tr>
                    <th className="dash-th-left">Account</th>
                    <th>Budget</th>
                    <th>Actuals</th>
                    <th>Variance</th>
                    <th className="dash-th-center">Category</th>
                  </tr>
                </thead>
                <tbody>
                  {top5Favorable.map((row, i) => {
                    const color = CATEGORY_COLORS[row.category] ?? "#64748b";
                    return (
                      <tr key={row.account} className={i % 2 === 1 ? "dash-tr-alt" : ""}>
                        <td className="dash-td-account" title={row.account}>{row.account}</td>
                        <td className="dash-td-num">{fmt(row.budget)}</td>
                        <td className="dash-td-num">{fmt(row.actual)}</td>
                        <td className="dash-td-num dash-fav">{fmtVar(row.variance)}</td>
                        <td className="dash-td-center">
                          <span className="dash-badge" style={{ background: color + "18", color }}>
                            {row.category}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                  {top5Favorable.length === 0 && (
                    <tr>
                      <td colSpan={5} className="dash-td-empty">
                        <span className="material-icons">info</span>
                        No under-budget accounts
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

          </div>
          </div>{/* /dash-tables-section-inner */}
          </div>{/* /dash-tables-section */}

          {/* ── Category Variance (matching ActualsDashboard style) ────────── */}
          {categoryVarData.length > 0 && (() => {
            const periodLabel = selectedMonth === "YTD" ? "YTD" : selectedMonth;
            const utilLabel   = quarterLabel || "YTD";
            return (
              <div className="dash-cat-table-wrap">
                <div className="ccx-header">
                  <span className="material-icons" style={{ fontSize: "16px", color: "#6366f1" }}>account_tree</span>
                  <span className="ccx-title">Category Variance</span>
                  <span className="ccx-subtitle">{periodLabel} · click a row to see account breakdown</span>
                  <span className="ccx-count">{categoryVarData.length} categories</span>
                </div>
                <table className="dash-cat-table dash-var-table">
                  <thead>
                    <tr>
                      <th className="dash-cat-th-left dash-var-col--left">Category</th>
                      <th className="dash-var-col--num">{periodLabel} Budget</th>
                      <th className="dash-var-col--num">{periodLabel} Actuals</th>
                      <th className="dash-var-col--num">{periodLabel} Variance</th>
                      <th className="dash-var-col--num">YTD</th>
                      <th className="dash-var-col--sm">{utilLabel} Util</th>
                      <th className="dash-var-col--num">YTG</th>
                      <th className="dash-var-col--bar">Account Breakdown</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categoryVarData.map(cat => {
                      const color = CATEGORY_COLORS[cat.category] ?? "#64748b";
                      const isExpanded = expandedCategories.has(cat.category);
                      return (
                        <React.Fragment key={cat.category}>
                          <tr
                            className={`dash-cat-tr${isExpanded ? " dash-cat-tr--active" : ""}`}
                            onClick={() => {
                              const next = new Set(expandedCategories);
                              if (isExpanded) next.delete(cat.category); else next.add(cat.category);
                              setExpandedCategories(next);
                            }}
                            title={`Click to see ${cat.category} accounts`}
                          >
                            <td className="dash-cat-td-name">
                              <span className="material-icons dash-cat-expand-icon">{isExpanded ? "expand_less" : "expand_more"}</span>
                              <span className="dash-cat-dot" style={{ background: color }} />
                              {cat.category}
                            </td>
                            <td className="dash-cat-td-num">{fmt(cat.budget)}</td>
                            <td className="dash-cat-td-num">{fmt(cat.actual)}</td>
                            <td className={`dash-cat-td-num ${cat.variance >= 0 ? "dash-fav" : "dash-unfav"}`}>
                              {fmtVar(cat.variance)}
                            </td>
                            <td className="dash-cat-td-num">{fmt(cat.ytdActual)}</td>
                            <td className={`dash-cat-td-num dash-cat-td-util ${cat.utilPct > 100 ? "dash-unfav" : ""}`}>
                              {cat.utilPct > 0 ? `${Math.round(cat.utilPct)}%` : "\u2014"}
                            </td>
                            <td className={`dash-cat-td-num ${cat.ytg < 0 ? "dash-unfav" : "dash-fav"}`}>
                              {fmtVar(cat.ytg)}
                            </td>
                            <td className="ccx-td-breakdown">
                              {cat.acctVars.length > 0 ? (
                                <div className="ccx-bar-stack">
                                  {cat.acctVars.map(av => {
                                    const pct = Math.abs(av.variance) / cat.totalAbsVar * 100;
                                    if (pct < 3) return null;
                                    return (
                                      <div
                                        key={av.account}
                                        className="ccx-bar-seg"
                                        style={{ width: `${pct}%`, background: color, opacity: av.variance > 0 ? 1 : 0.4 }}
                                        title={`${av.account}: ${fmtVar(av.variance)}`}
                                      />
                                    );
                                  })}
                                </div>
                              ) : (
                                <span style={{ color: "#9ca3af", fontSize: "11px" }}>{"\u2014"}</span>
                              )}
                            </td>
                          </tr>

                          {/* Expanded: account breakdown */}
                          {isExpanded && (
                            <tr className="ccx-expand-row">
                              <td colSpan={8}>
                                <div className="ccx-expand-inner">
                                  <table className="ccx-cat-table">
                                    <thead>
                                      <tr>
                                        <th className="ccx-th-left">Account</th>
                                        <th className="ccx-th-num">Budget</th>
                                        <th className="ccx-th-num">Actuals</th>
                                        <th className="ccx-th-num">Variance</th>
                                        {quarterLabel && (
                                          <>
                                            <th className="ccx-th-num">{quarterLabel} Budget</th>
                                            <th className="ccx-th-num">{quarterLabel} Util %</th>
                                            <th className="ccx-th-num">YTG Budget</th>
                                          </>
                                        )}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {cat.accounts.map(row => {
                                        const rowVar = row.budget - row.actual;
                                        const isAcctSelected = selectedAccount === row.account;
                                        return (
                                          <tr
                                            key={row.account}
                                            className={`ccx-cat-row ccx-cat-row--clickable${isAcctSelected ? " ccx-cat-row--selected" : ""}`}
                                            onClick={(e) => { e.stopPropagation(); setSelectedAccount(isAcctSelected ? null : row.account); }}
                                            title={isAcctSelected ? "Click to clear account filter" : "Click to filter chart by this account"}
                                          >
                                            <td className="ccx-cat-td-name" style={{ paddingLeft: "12px" }}>
                                              {isAcctSelected && <span className="material-icons" style={{ fontSize: 14, verticalAlign: "middle", marginRight: 4, color: CATEGORY_COLORS[cat.category] ?? "#4A90E2" }}>filter_alt</span>}
                                              {row.account}
                                            </td>
                                            <td className="ccx-td-num">{fmt(row.budget)}</td>
                                            <td className="ccx-td-num">{fmt(row.actual)}</td>
                                            <td className={`ccx-td-num ${rowVar >= 0 ? "dash-fav" : "dash-unfav"}`}>
                                              {fmtVar(rowVar)}
                                            </td>
                                            {quarterLabel && (
                                              <>
                                                <td className="ccx-td-num">{fmt(row.qtrBudget)}</td>
                                                <td className={`ccx-td-num ${row.utilPct > 100 ? "dash-unfav" : ""}`}>
                                                  {row.qtrBudget > 0 ? `${Math.round(row.utilPct)}%` : "-"}
                                                </td>
                                                <td className="ccx-td-num">{fmt(row.ytgBudget)}</td>
                                              </>
                                            )}
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}

          {/* ── Spend Chart (bar / line toggle) ──────────────────────────────── */}
          {spendChartMonthly.length > 0 && availableMonths.length > 0 && (() => {
            const C_BUDGET = "#4A90E2";
            const C_ACTUAL = "#0891b2";

            // Build cumulative data for line chart
            let cBudget = 0, cActual = 0;
            const chartData = spendChartMonthly.map(d => {
              cBudget += d.mBudgetForecast;
              if (d.mActual !== null) cActual += d.mActual;
              return {
                name: d.short,
                budget: d.mBudgetForecast,
                actuals: d.mActual,
                cBudget,
                cActuals: d.mActual !== null ? cActual : null,
              };
            });

            const fmtAxis = (v: number) => { const a = Math.abs(v); return v === 0 ? "$0" : a >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : a >= 1e3 ? `$${(v / 1e3).toFixed(0)}k` : `$${Math.round(v)}`; };
            const fmtBarLabel = (v: any) => {
              const n = Number(v);
              if (!n) return "";
              const abs = Math.abs(n);
              return abs >= 1e6 ? `$${(n / 1e6).toFixed(1)}M` : abs >= 1e3 ? `$${(n / 1e3).toFixed(0)}k` : `$${Math.round(n)}`;
            };
            const fmtTipVal = (v: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(v);

            const CustomTooltip = ({ active, payload, label }: any) => {
              if (!active || !payload?.length) return null;
              return (
                <div className="dash-recharts-tooltip">
                  <div className="dash-recharts-tooltip-label">{label}</div>
                  {payload.map((entry: any, i: number) => (
                    <div key={i} className="dash-recharts-tooltip-row">
                      <span className="dash-recharts-tooltip-dot" style={{ background: entry.color }} />
                      <span className="dash-recharts-tooltip-name">{entry.name}</span>
                      <span className="dash-recharts-tooltip-val">{fmtTipVal(entry.value)}</span>
                    </div>
                  ))}
                </div>
              );
            };

            const SeriesLegend = ({ payload }: any) => (
              <div className="dash-chart-legend-custom">
                {payload?.map((entry: any, i: number) => (
                  <span key={i} className="dash-chart-legend-item">
                    <span className="dash-chart-legend-dot" style={{ background: entry.color }} />
                    {entry.value}
                  </span>
                ))}
              </div>
            );

            return (
              <div className="dash-chart-card">
                <div className="dash-chart-header">
                  <span className="material-icons">{chartType === "bar" ? "bar_chart" : "show_chart"}</span>
                  {chartType === "bar" ? "Monthly Spend" : "Full-Year Trajectory"}
                  <span className="dash-chart-subtitle">
                    {chartType === "bar" ? "Budget vs Actuals by month" : "Cumulative budget vs actuals"}
                  </span>
                </div>
                <div className="dash-chart-body">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                  <div />
                  <div className="dash-chart-toggle">
                    <button
                      className={`dash-chart-toggle-btn${chartType === "bar" ? " dash-chart-toggle-btn--active" : ""}`}
                      onClick={() => setChartType("bar")}
                    >
                      <span className="material-icons" style={{ fontSize: 16 }}>bar_chart</span>
                      Bar
                    </button>
                    <button
                      className={`dash-chart-toggle-btn${chartType === "line" ? " dash-chart-toggle-btn--active" : ""}`}
                      onClick={() => setChartType("line")}
                    >
                      <span className="material-icons" style={{ fontSize: 16 }}>show_chart</span>
                      Line
                    </button>
                  </div>
                </div>

                {/* Clickable category filter chips */}
                <div className="dash-chart-cat-strip">
                  <button
                    className={`dash-chart-cat-chip${selectedCategory === null ? " dash-chart-cat-chip--active" : ""}`}
                    onClick={() => setSelectedCategory(null)}
                  >
                    All
                  </button>
                  {categorySummaries.map(cat => {
                    const color = CATEGORY_COLORS[cat.category] ?? "#64748b";
                    const isActive = selectedCategory === cat.category;
                    return (
                      <button
                        key={cat.category}
                        className={`dash-chart-cat-chip${isActive ? " dash-chart-cat-chip--active" : ""}`}
                        style={isActive ? { background: color + "14", color, borderColor: color + "60" } : undefined}
                        onClick={() => setSelectedCategory(isActive ? null : cat.category)}
                      >
                        <span className="dash-chart-cat-chip-dot" style={{ background: color }} />
                        {cat.category}
                      </button>
                    );
                  })}
                </div>

                {/* Active account filter badge */}
                {selectedAccount && (
                  <div className="dash-acct-filter-strip">
                    <span className="material-icons" style={{ fontSize: 14, color: "#4A90E2" }}>filter_alt</span>
                    <span className="dash-acct-filter-label">Showing: <strong>{selectedAccount}</strong></span>
                    <button className="dash-acct-filter-clear" onClick={() => setSelectedAccount(null)} title="Clear account filter">
                      <span className="material-icons" style={{ fontSize: 14 }}>close</span>
                    </button>
                  </div>
                )}

                <div className="dash-chart-body">
                  <ResponsiveContainer width="100%" height={240}>
                    {chartType === "bar" ? (
                      <BarChart data={chartData} margin={{ top: 16, right: 12, left: 0, bottom: 0 }} barGap={2} barCategoryGap="20%">
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                        <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#64748b" }} />
                        <YAxis axisLine={false} tickLine={false} tickFormatter={fmtAxis} tick={{ fontSize: 12, fill: "#64748b" }} width={56} />
                        <Tooltip content={<CustomTooltip />} cursor={{ fill: "oklch(0.97 0.005 260)" }} />
                        <Legend content={<SeriesLegend />} />
                        <Bar dataKey="budget" name="Budget" fill={C_BUDGET} radius={[3, 3, 0, 0]}>
                          <LabelList dataKey="budget" position="top" formatter={fmtBarLabel} style={{ fontSize: 11, fill: "#4A90E2", fontWeight: 600 }} />
                        </Bar>
                        <Bar dataKey="actuals" name="Actuals" fill={C_ACTUAL} radius={[3, 3, 0, 0]}>
                          <LabelList dataKey="actuals" position="top" formatter={fmtBarLabel} style={{ fontSize: 11, fill: "#0891b2", fontWeight: 600 }} />
                        </Bar>
                      </BarChart>
                    ) : (
                      <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                        <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#64748b" }} />
                        <YAxis axisLine={false} tickLine={false} tickFormatter={fmtAxis} tick={{ fontSize: 12, fill: "#64748b" }} width={56} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend content={<SeriesLegend />} />
                        <Line type="monotone" dataKey="cBudget" name="Budget" stroke={C_BUDGET} strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="cActuals" name="Actuals" stroke={C_ACTUAL} strokeWidth={2.5} dot={{ r: 3, fill: C_ACTUAL, strokeWidth: 1.5, stroke: "#fff" }} connectNulls={false} />
                      </LineChart>
                    )}
                  </ResponsiveContainer>
                </div>
                </div>{/* /dash-chart-body */}
              </div>
            );
          })()}

        </>
      )}

      {!loading && !selectedMonth && availableMonths.length === 0 && (
        <div className="dash-empty-state">
          <span className="material-icons">bar_chart</span>
          <div>No actuals data available yet for this cost center.</div>
          <div>Upload actuals to view the dashboard.</div>
        </div>
      )}

      {/* Action Plan slide-in panel */}
      <ActionPlanPanel
        open={actionPlanOpen}
        onClose={() => { setActionPlanOpen(false); fetchActionCount(); }}
        costCenterNumber={costCenterNumber}
        costCenterName={costCenterName}
        fiscalYear={selectedFY}
        month={selectedMonth}
        categories={CATEGORY_ORDER}
      />

    </div>
  );
};

export default Dashboard;
